package mapsearch.globalsearch

import apiclient.customfields.FieldValue
import apiclient.customfields.tagValues
import apiclient.geoobjects.LatLon
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.ReadOnlyTags
import apiclient.mapsearch.SearchSpec
import apiclient.tags.addMultivaluedTag
import apiclient.tags.init
import apiclient.tags.parseTag
import apiclient.tags.removeTag
import apiclient.tags.tag
import auth.CurrentWorkspaceStore
import auth.WorkspacesStore
import dev.fritz2.core.RootStore
import koin.koinCtx
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapNotNull
import map.MapStateStore
import mapsearch.SearchLead
import mapsearch.SearchSwitch
import mapsearch.isActive
import model.KeywordTag
import search.global.ActiveGlobalSearchOptionsStore
import search.global.ActiveSearchFieldValuesStore
import search.global.ActiveSearchKeywordsStore
import search.global.ActiveSearchObjectTypesStore
import search.global.ActiveSearchReadOnlyKeywordsStore
import search.global.SearchInputFieldStore
import search.searchFilterStores.SearchFilterOption
import utils.NULL_ISLAND

val globalSearchObjectTypes = ObjectType.entries.filter { objectType ->
    when (objectType) {
        // filter out ObjectTypes we don't want to see in search
        ObjectType.Floor,
        ObjectType.Unit,
        ObjectType.Notification,
        ObjectType.TransientMarker,
        ObjectType.HistoryEntry -> false

        else -> true
    } && objectType.mapTileAPI
}

class GlobalSearchSpecStore : RootStore<SearchSpec>(
    SearchSpec(
        ownerIds = emptyList(),
        objectTypes = globalSearchObjectTypes,
    ),
    Job(),
) {

//    private val workspacesStore: WorkspacesStore by koinCtx.inject()
    private val currentWorkspaceStore = koinCtx.get<CurrentWorkspaceStore>()
    private val searchSwitch: SearchSwitch by koinCtx.inject()
    private val mapStateStore: MapStateStore by koinCtx.inject()
    private val searchInputFieldStore: SearchInputFieldStore by koinCtx.inject()
    private val activeSearchKeyWordsStore: ActiveSearchKeywordsStore by koinCtx.inject()
    private val activeSearchObjectTypesStore: ActiveSearchObjectTypesStore by koinCtx.inject()
    private val activeSearchReadOnlyKeywordsStore: ActiveSearchReadOnlyKeywordsStore by koinCtx.inject()
    private val activeSearchFieldValuesStore: ActiveSearchFieldValuesStore by koinCtx.inject()
    private val activeGlobalSearchOptionsStore: ActiveGlobalSearchOptionsStore by koinCtx.inject()

    private val setOwnerIds = handle<List<String>> { current, ownerIds ->
        current.copy(ownerIds = ownerIds)
    }

    private val updateNearCoordinate = handle<LatLon> { current, near ->
        if (near != LatLon.Companion.NULL_ISLAND) {
            current.copy(near = near)
        } else current
    }

    private val updateGlobalSearchTextQuery = handle<String> { current, textQuery ->
        if (textQuery.isBlank()) {
            current.copy(query = null)
        } else current.copy(query = textQuery)
    }

    private val updateGlobalSearchTagsQuery = handle<List<String>?> { current, tags ->
        val currentOtherTags = current.tags?.removeTag(ObjectTags.Keyword) ?: emptyList()
        if (!tags.isNullOrEmpty()) {
            current.copy(tags = currentOtherTags + tags.map { ObjectTags.Keyword.tag(it) })
        } else {
            current.copy(tags = currentOtherTags)
        }
    }

    private val updateGlobalSearchObjectTypes = handle<List<ObjectType>?> { current, objTypes ->
//        console.log("UPDATE Global Search ctx objectType with", objTypes.toString())
//        console.log("UPDATED Global Search ctx:", current.copy(objectTypes = objTypes).toString())
        current.copy(
            objectTypes = objTypes?.takeUnless { it.isEmpty() }
                ?: globalSearchObjectTypes,
        )
    }

    private val updateGlobalSearchOtherTags = handle<List<KeywordTag>?> { current, otherTags ->
        val currentOtherTags = current.tags?.removeTag(ReadOnlyTags.Assignee)?.removeTag(ReadOnlyTags.Creator) ?: emptyList()
        if (!otherTags.isNullOrEmpty()) {
            current.copy(
                tags = currentOtherTags + otherTags.map { keywordTag ->
                    if (!keywordTag.readOnlyStringValue.isNullOrBlank()) {
                        when (keywordTag.readOnlyType) {
                            ReadOnlyTags.Assignee -> ReadOnlyTags.Assignee.tag(keywordTag.readOnlyStringValue)
                            ReadOnlyTags.Creator -> ReadOnlyTags.Creator.tag(keywordTag.readOnlyStringValue)
                            else -> keywordTag.fieldText
                        }
                    } else keywordTag.fieldText
                },
            )
        } else {
            current.copy(tags = currentOtherTags)
        }
    }
    private val updateGlobalSearchFieldValueTags = handle<List<FieldValue>> { current, fieldValues ->
        val currentOtherTags = current.tags?.removeTag(ObjectTags.CustomField) ?: emptyList()
        if (fieldValues.isNotEmpty()) {
            current.copy(tags = currentOtherTags.addMultivaluedTag(ObjectTags.CustomField, fieldValues.tagValues()))
        } else {
            current.copy(tags = currentOtherTags)
        }
    }

    private val updateSearchOptionsInGlobalSearch = handle<Map<SearchFilterOption, Boolean>> { current, options ->
        console.log(
            "Set SearchOptions in GlobalSearch to",
            options.map { it.key.tagToToggle.parseTag().first to it.value }.toString(),
        )
        val addToExclude = options.filter { !it.value }.map { it.key.tagToToggle }
        val removeFromExclude = options.filter { it.value }.map { it.key.tagToToggle }
        val filtered = current.excludeTags?.filter { tag ->
            tag !in removeFromExclude
        }
        current.copy(
            excludeTags = filtered.init() + addToExclude,
        )
    }

    init {
        currentWorkspaceStore.data.filterNotNull().map { group ->
            (group.sharedLayerDefinitions?.map { l -> l.layerId }.orEmpty() + group.groupId).distinct()
        }.filter { it.isNotEmpty() } handledBy setOwnerIds

        data handledBy { globalSearchSpec ->
            searchSwitch.update(
                if (globalSearchSpec.isActive()) {
                    if (searchSwitch.current != SearchLead.Global) {
                        console.log("-> Switch to Global Search")
                    }
                    SearchLead.Global
                } else {
                    if (searchSwitch.current != SearchLead.Map) {
                        console.log("-> Switch to Map Search")
                    }
                    SearchLead.Map
                },
            )
        }
        mapStateStore.data.mapNotNull { it?.center } handledBy updateNearCoordinate
        searchInputFieldStore.data handledBy updateGlobalSearchTextQuery
        activeSearchKeyWordsStore.data handledBy updateGlobalSearchTagsQuery
        activeSearchObjectTypesStore.data handledBy updateGlobalSearchObjectTypes
        activeSearchReadOnlyKeywordsStore.data handledBy updateGlobalSearchOtherTags
        activeSearchFieldValuesStore.data handledBy updateGlobalSearchFieldValueTags
        activeGlobalSearchOptionsStore.data handledBy updateSearchOptionsInGlobalSearch
    }
}
